<template>
  <div>
    <div class="referrals-table--wrap">
      <div class="table--wrap" ref="listTable">
        <p class="content__title">{{ $t('referral.period') }}</p>
        <ui-selectbox
          class="some-class"
          :value="duration"
          :items="durationList"
          :width="'175px'"
          :hideLabel="true"
          @input="onDurationChange"
        />
        <app-pagination
          class="clearfix"
          :total="count"
          :page="page"
          :limit="limit"
          :pagingBlock="5"
          @onPagingOptionChange="onPagingOptionChange" />

        <data-table :headers="headers" :sortType="sort" @changeSort="changeSort">
          <template v-slot:colgroup>
            <colgroup>
              <col width="7.1%">
              <col width="18.5%">
              <col width="16.4%">
              <col width="11.9%">
              <col width="16.6%">
              <col width="16.6%">
              <col width="12.7%">
            </colgroup>
          </template>
          <template v-if="referralData.length">
            <tbody>
            <tr v-for="(item, index) in referralData" :key="index">
              <td class="data-detail">
                <button @click="selectUserDetail(item)">
                  <i class="icon icon--document-black"></i>
                </button>
              </td>
              <td class="data-userName">
                <span>{{ item.deleted ? '-' :item.userName }}</span>
              </td>
              <td class="data-user">{{ item.deleted ? $t('referral.referrals.history.withdrawn') : item.userId }}</td>
              <td class="data-regDate">{{ item.regDate | dashDate }}</td>
              <td class="data-plan">
                <span v-if="item.deleted">-</span>
                <span v-else-if="!item.beusPlanName || item.beusDeactivate">{{ $t('referral.referrals.history.notReferred') }}</span>
                <span v-else :class="item.beusPaid ? 'paid' : 'free'">
                  <span>{{ item.beusPaid ? $t('referral.referrals.history.paid') : $t('referral.referrals.history.free') }}({{ item.beusPlanName }})</span>
                  <span>{{ item.beusEndDate | dashDate }} {{ $t('referral.referrals.history.end') }}</span>
                </span>
              </td>
              <td class="data-plan">
                <span v-if="item.deleted">-</span>
                <span v-else-if="!item.baPlanName || item.baDeactivate">{{ $t('referral.referrals.history.notReferred') }}</span>
                <span v-else :class="item.baPaid ? 'paid' : 'free'">
                  <span>{{ item.baPaid ? $t('referral.referrals.history.paid') : $t('referral.referrals.history.free') }}({{ item.baPlanName }})</span>
                  <span>{{ item.baEndDate | dashDate }} {{ $t('referral.referrals.history.end') }}</span>
                </span>
              </td>
              <td class="data-reward">
                <span v-if="item.deleted">-</span>
                <span v-else>{{ item.reward | thousand }} JAM</span>
              </td>
            </tr>
            </tbody>
          </template>
          <tbody v-else>
          <tr class="no-data">
            <td class="no-data" colspan="7">
              <div>
                <i class="icon icon--no-list" />
                <p v-html="$t('referral.referrals.history.noReferral')"></p>
              </div>
            </td>
          </tr>
          </tbody>
        </data-table>
      </div>
    </div>
    <Dimmed v-if="selectedUser" @close="unselectUserDetail">
      <referral-detail :userData="selectedUser" :referralCode="referralCode" @confirm="unselectUserDetail" @close="unselectUserDetail" />
    </Dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { parse, startOfDay, addDays, addMonths, format } from 'date-fns'

import { EventBus } from '@/libs/event-bus'
import { getReferralStateList } from '@/api/referral'

import AppPagination from '@/components/ui/AppPagination'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DataTable from '@/components/ui/DataTable'
import Dimmed from '@/components/ui/Dimmed'
import ReferralDetail from '@/components/user-info/manage-plan/ReferralDetail.vue'

export default {
  components: {
    AppPagination,
    UiSelectbox,
    DataTable,
    Dimmed,
    ReferralDetail
  },
  props: {
    referralCode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isLoading: true,
      page: 1,
      limit: 30,
      sort: '-regDate',
      minDate: null,
      maxDate: null,
      dateRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      showReferralDetail: false,
      headers: [
        {
          name: this.$t('common.detail'),
          sortable: false,
          value: 'detail'
        },
        {
          name: this.$t('referral.referrals.history.userName'),
          sortable: true,
          value: 'userName'
        },
        {
          name: this.$t('referral.referrals.history.id'),
          sortable: true,
          value: 'userId'
        },
        {
          name: this.$t('referral.referrals.history.regDate'),
          sortable: true,
          value: 'regDate'
        },
        {
          name: 'UX Heatmap',
          sortable: true,
          value: 'beusPlanName'
        },
        {
          name: 'Journey Map',
          sortable: true,
          value: 'baPlanName'
        },
        {
          name: this.$t('referral.referrals.history.reward'),
          value: 'reward'
        }
      ],
      duration: 0,
      durationList: [
        {
          value: 0,
          text: this.$t('common.all')
        },
        {
          value: 3,
          text: this.$t('common.recentMonths', { month: '3' })
        },
        {
          value: 6,
          text: this.$t('common.recentMonths', { month: '6' })
        },
        {
          value: 12,
          text: this.$t('common.recentMonths', { month: '12' })
        },
        {
          value: 24,
          text: this.$t('common.recentMonths', { month: '24' })
        }
      ],
      referralData: [],
      count: 0,
      selectedUser: null
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ])
  },
  created () {
    const today = startOfDay(new Date())
    const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
    const maxDate = parse(today)
    const endDate = parse(today)
    let startDate = minDate

    if (startDate < minDate) {
      startDate = parse(minDate)
    }

    this.minDate = minDate
    this.maxDate = maxDate

    this.dateRange = {
      startDate,
      endDate
    }
    this.loadData()
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
  },
  methods: {
    loadData () {
      this.isLoading = true
      const startDate = this.dateRange.startDate
      const endDate = this.dateRange.endDate

      const payload = {
        startDate: null,
        endDate: null,
        page: this.page - 1,
        limit: this.limit,
        order: this.sort
      }

      if (startDate && endDate) {
        payload.startDate = format(startDate, 'YYYY-MM-DD')
        payload.endDate = format(endDate, 'YYYY-MM-DD')
      }

      getReferralStateList(payload).then(res => {
        if (res) {
          this.referralData = res.list
          this.count = res.count
        }
      }).catch(e => {
        console.log(e)
      }).finally(() => {
        this.isLoading = false
      })
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    onRangeChange (data) {
      this.dateRange = data.range
      this.loadData()
      this.closePicker()
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
      this.loadData()
    },
    onDurationChange (duration) {
      const today = startOfDay(new Date())
      const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
      const endDate = parse(today)
      let startDate = addMonths(today, -duration)
      if (duration === 0) {
        startDate = addDays(parse(this.userData.regDate), -1)
      }

      if (startDate < minDate) {
        startDate = parse(minDate)
      }

      this.duration = duration
      this.page = 1

      this.onRangeChange({
        range: {
          startDate,
          endDate
        }
      })
    },
    changeSort (sort) {
      this.sort = sort
      this.page = 1
      this.loadData()
    },
    selectUserDetail (userData) {
      this.selectedUser = userData
    },
    unselectUserDetail () {
      this.selectedUser = null
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.referrals-table--wrap {
  padding: 15px 25px 30px 25px;
  margin-top: 30px;
  box-sizing: border-box;
  border-radius: 14px;
  box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;

  .content {
    text-align: center;
  }
  .content__title {
    font-size: 15px;
    font-weight: 500;
    color: #2f2f2f;
    display: inline-block;
    padding: 11px 0 20px 0;
  }

  .ui_select {
    vertical-align: middle;
    transform: translateY(-2px);
    margin-left: 10px;
    position: relative;
    z-index: 2;
  }

  .page__comm--wrap {
    float: right;
    width: auto;
    margin-bottom: 15px;
  }

  .table--wrap {
    /deep/ table {
      thead {
        tr {
          background-color: #333;
          th {
            padding: 6px 12px 8px 12px;
            text-align: start;
            vertical-align: bottom;
            box-sizing: border-box;
            span {
              line-height: 1.2;
              vertical-align: bottom;
            }
            &:first-of-type:before,
            &:last-of-type:before {
              background-color: #333;
            }
            &.data-detail,
            &.data-regDate {
              text-align: center;
            }
            &.data-reward {
              text-align: end;
            }
          }
        }
      }
    }
    tbody tr {
      border-top: 1px solid #ebebeb;
      text-align: start;

      &:first-child {
        border-top: none
      }

      &:last-child {
        border-bottom: 1px solid #ebebeb;

        &.no-data {
          border-bottom: none;
        }
      }

      td {
        height: 60px;
        padding: 12px;
        font-size: 13px;
        line-height: 1.2;
        color: #444;
        position: relative;
        box-sizing: border-box;

        &.data-userName {
          font-weight: 600;
        }

        &.data-detail {
          text-align: center;
          button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border-radius: 4px;
            border: 1px solid #BBB;
            background: #FFF;
          }
        }
        &.data-regDate {
          text-align: center;
        }
        &.data-plan {
          > span {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            color: #666;
            font-size: 12px;
            font-weight: 500;

            &.paid {
              color: #E60724;
            }
            &.free {
              color: #13A5BF;
            }
          }
        }
        &.data-reward {
          text-align: end;
        }
      }
    }
  }
}

.no-data {
  width: 100%;
  height: 100px;
  padding-top: 15px;
  position: relative;

  & > div {
    width: 100%;
    min-height: 366px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &> p {
      text-align: center;
    }
  }

}
</style>
